import React, { useState } from "react";

import {
  faBook,
  faBracketsCurly,
  faBug,
  faChartSimple,
  faDatabase,
  faPlay,
  faSliders,
  faVial,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import { observer } from "mobx-react-lite";
import { useSearchParams } from "react-router-dom";

import { AnalyticsSidePane } from "src/analytics/AnalyticsSidePane";
import { useTrafficPolicies } from "src/api/queries";
import { DatasetPurpose } from "src/api/types";
import { ResourceType } from "src/clients/flow-api";
import { SimpleRoleType } from "src/clients/taktile-api";
import { AssembleDatasetModal } from "src/datasets/AssembleDatasetModal/Modal";
import { DatasetSidePane } from "src/datasets/DatasetSidePane";
import { LockableNavIconContainer } from "src/flowContainer/FlowSideBar/LockableNavIconContainer";
import { NavIconContainer } from "src/flowContainer/FlowSideBar/NavIconContainer";
import { TestRunsSidePane } from "src/flowContainer/TestRunsSidePane/TestRunsSidePane";
import { GlobalParametersSidePane } from "src/globalParameters/GlobalParametersSidePane";
import { useCapabilities } from "src/hooks/useCapabilities";
import { flowFolderScope } from "src/hooks/useCapabilities/flowFolderScope";
import { useIsIcebergLongTestRunsEnabled } from "src/hooks/useIsIcebergLongTestRunsEnabled";
import { DebugSidePane } from "src/powertools/DebugPane";
import { usePowertools } from "src/powertools/hooks";
import { useAuthoringContext } from "src/router/routerContextHooks";
import {
  getCurrentLeftPane,
  LeftPaneOptions,
  useToggleLeftSidePaneOption,
} from "src/router/SearchParams";
import { AuthorPageParams, getApiDocsUrl } from "src/router/urls";
import { SchemaEditSidePane } from "src/schema/SchemaPane";
import { useCaseTemplateInputSchemaError } from "src/schema/schemaEditor/useCaseTemplateInputSchemaValidation";
import {
  useShowCaseTemplateVersionArchivedToast,
  useShowCaseTemplateVersionChangedToast,
} from "src/schema/schemaEditor/useErrorToasts";
import { SimulationsSidePane } from "src/simulations/SimulationsSidePane";
import { useSimulationAccess } from "src/simulations/useSimulationAccess";
import { useSimulationRuns } from "src/simulations/useSimulationRun";
import {
  useInputSchemaError,
  useOutputSchemaError,
} from "src/store/runState/inputOutputErrors";
import { useParamsDecode } from "src/utils/useParamsDecode";
import { DecisionHistorySidePane } from "src/versionDecisionHistory/SidePane";

type SchemaIconProps = {
  schemaInvalid: boolean;
  toggleSchemaPane: () => void;
  currentOpenPane: LeftPaneOptions | null;
  flowVersionId: string;
};

/** Subscribes to specifc observers for displaying schema errors which can not really be generalised. */
const SchemaIcon: React.FC<SchemaIconProps> = observer(
  ({ schemaInvalid, toggleSchemaPane, currentOpenPane, flowVersionId }) => {
    const { version } = useAuthoringContext();
    const inputSchemaError = useInputSchemaError(version.input_schema);
    const outputSchemaError = useOutputSchemaError();

    return (
      <LockableNavIconContainer
        currentOpenPane={currentOpenPane}
        errored={schemaInvalid || inputSchemaError || outputSchemaError}
        flowVersionId={flowVersionId}
        icon={faBracketsCurly}
        lockResourceType={ResourceType.SCHEMA}
        selected={currentOpenPane === LeftPaneOptions.Schema}
        onClick={toggleSchemaPane}
        dataLoc="show-schemas-pane"
        tooltip={{
          body: "Configure the input and output fields for this Decision Flow",
          title: "Schemas",
        }}
      />
    );
  },
);

export const FlowSideBar: React.FC = () => {
  const isPowertoolsEnabled = usePowertools();
  const isLongTestRunsEnabled = useIsIcebergLongTestRunsEnabled();

  const { role, datasets, decisionHistory, flowCharts, flowVersions } =
    useCapabilities();
  const [searchParams] = useSearchParams();
  const [schemaInvalid, setSchemaInvalid] = useState(false);
  const { flow_id, version_id, orgId, wsId } =
    useParamsDecode<AuthorPageParams>();

  const { version, simulationId } = useAuthoringContext();
  const { simulationRuns } = useSimulationRuns(flow_id, version_id);
  const error = useCaseTemplateInputSchemaError(version.input_schema);

  const analyticsDisabled =
    !simulationId &&
    (simulationRuns === undefined || simulationRuns?.length !== 0);

  useShowCaseTemplateVersionArchivedToast(error === "archived");
  useShowCaseTemplateVersionChangedToast(
    error === "inconsistent" || error === "corrupted",
    version.input_schema,
    version_id,
  );

  const currentOpenPane = getCurrentLeftPane(searchParams);
  const toggleSidePaneOptions = useToggleLeftSidePaneOption();
  const toggleDatasetsPane = () =>
    toggleSidePaneOptions(LeftPaneOptions.Datasets);
  const toggleSchemaPane = () => toggleSidePaneOptions(LeftPaneOptions.Schema);
  const toggleDecisionHistorySidePane = () =>
    toggleSidePaneOptions(LeftPaneOptions.DecisionHistory);
  const toggleParametersSidePane = () =>
    toggleSidePaneOptions(LeftPaneOptions.Parameters);
  const toggleDebugSidePane = () =>
    toggleSidePaneOptions(LeftPaneOptions.Debug);
  const toggleSimulationsSidePane = () =>
    toggleSidePaneOptions(LeftPaneOptions.Simulations);
  const toggleAnalyticsSidePane = () =>
    toggleSidePaneOptions(LeftPaneOptions.Analytics);
  const toggleTestRunsSidePane = () =>
    toggleSidePaneOptions(LeftPaneOptions.TestRuns);
  const simulationAccess = useSimulationAccess();

  const datasetsTooltipTitle = simulationAccess.isConfigured
    ? "Datasets"
    : "Test data";
  const datasetsTooltipBody = simulationAccess.isConfigured
    ? "Upload and manage data to test or simulate this Decision Flow"
    : "Upload and manage test data to test this Decision Flow";

  const renderSideBarButtonsContainer = () => (
    <div className="z-20 flex w-13 flex-col items-center justify-between border-gray-200 border-r border-l bg-white py-4">
      <div className="flex flex-col items-center gap-y-2">
        {datasets.canAccess(flowFolderScope(flow)) && !simulationId && (
          <NavIconContainer
            tooltip={{
              body: datasetsTooltipBody,
              title: datasetsTooltipTitle,
            }}
            dataLoc="show-datasets-pane"
            icon={faDatabase}
            selected={currentOpenPane === LeftPaneOptions.Datasets}
            onClick={toggleDatasetsPane}
          />
        )}
        <SchemaIcon
          currentOpenPane={currentOpenPane}
          flowVersionId={version_id}
          schemaInvalid={schemaInvalid}
          toggleSchemaPane={toggleSchemaPane}
        />
        <LockableNavIconContainer
          currentOpenPane={currentOpenPane}
          flowVersionId={version_id}
          tooltip={{
            body: "Create and edit Parameters for this Decision Flow version.",
            title: "Parameters",
          }}
          dataLoc="show-global-parameters-pane"
          icon={faSliders}
          lockResourceType={ResourceType.PARAMETER}
          selected={currentOpenPane === LeftPaneOptions.Parameters}
          onClick={toggleParametersSidePane}
        />
        {decisionHistory.canAccess(flowFolderScope(flow)) && !simulationId && (
          <NavIconContainer
            tooltip={{
              body: "See the decision history of the selected Flow version.",
              title: "Decision history",
            }}
            dataLoc="show-decision-history-pane"
            icon={faWavePulse}
            selected={currentOpenPane === LeftPaneOptions.DecisionHistory}
            onClick={toggleDecisionHistorySidePane}
          />
        )}
        {!simulationId && (
          <NavIconContainer
            tooltip={{
              body: "View API documentation for this Decision Flow",
              title: "API docs",
            }}
            dataLoc="show-api-docs"
            icon={faBook}
            linkTarget={getApiDocsUrl(orgId, wsId, flow_id, version_id)}
            selected={false}
          />
        )}
        {isLongTestRunsEnabled &&
          flowVersions.canEdit(flowFolderScope(flow)) && (
            <NavIconContainer
              tooltip={{
                body: "Run a test to validate your decision logic, including Agents.",
                title: "Test runs for Agents",
              }}
              dataLoc="show-test-runs"
              icon={faPlay}
              selected={currentOpenPane === LeftPaneOptions.TestRuns}
              onClick={toggleTestRunsSidePane}
            />
          )}
        {!simulationId && simulationAccess.canAccess && (
          <NavIconContainer
            tooltip={{
              body: "Simulate your Decision Flow with large amounts of data",
              title: "Simulations",
            }}
            dataLoc="show-simulations"
            icon={faVial}
            selected={currentOpenPane === LeftPaneOptions.Simulations}
            onClick={toggleSimulationsSidePane}
          />
        )}
        {flowCharts.canAccess(flowFolderScope(flow)) && (
          <NavIconContainer
            dataLoc="show-analytics"
            tooltip={{
              body: analyticsDisabled
                ? "Flow-level analytics are paused while simulations are present. Open a specific simulation to explore its metrics."
                : "Visualize the output from test runs to gain insights into data and decision logic",
              title: analyticsDisabled ? "Analytics unavailable" : "Analytics",
            }}
            disabled={analyticsDisabled}
            icon={faChartSimple}
            selected={currentOpenPane === LeftPaneOptions.Analytics}
            onClick={toggleAnalyticsSidePane}
          />
        )}
        {role === SimpleRoleType.ADMIN && isPowertoolsEnabled && (
          <NavIconContainer
            tooltip={{
              body: "Debug features for @taktile employees only.",
              title: "@taktile debug features (INTERNAL)",
            }}
            dataLoc="show-taktile-debug-pane"
            icon={faBug}
            selected={currentOpenPane === LeftPaneOptions.Debug}
            onClick={toggleDebugSidePane}
          />
        )}
      </div>
    </div>
  );

  const renderSidePane = (openPane: LeftPaneOptions) => {
    return (
      <div
        className="absolute left-[calc(3.25rem-1px)] z-10 h-full shadow-lg"
        data-loc={`side-pane-${openPane}`}
      >
        {
          {
            [LeftPaneOptions.Parameters]: (
              <GlobalParametersSidePane
                onRequestClose={toggleParametersSidePane}
              />
            ),
            [LeftPaneOptions.DecisionHistory]: decisionHistory.canAccess(
              flowFolderScope(flow),
            ) ? (
              <DecisionHistorySidePane
                onRequestClose={toggleDecisionHistorySidePane}
              />
            ) : null,
            [LeftPaneOptions.Datasets]: (
              <DatasetSidePane
                onAssembleDataset={setShowAssembleModal}
                onRequestClose={toggleDatasetsPane}
              />
            ),
            [LeftPaneOptions.Schema]: (
              <SchemaEditSidePane
                setSchemaInvalid={setSchemaInvalid}
                onRequestClose={toggleSchemaPane}
              />
            ),
            [LeftPaneOptions.Debug]: (
              <DebugSidePane onRequestClose={toggleDebugSidePane} />
            ),
            [LeftPaneOptions.Simulations]: simulationAccess.canAccess ? (
              <SimulationsSidePane
                onAssembleDataset={setShowAssembleModal}
                onRequestClose={toggleSimulationsSidePane}
              />
            ) : null,
            [LeftPaneOptions.TestRuns]: flowVersions.canEdit(
              flowFolderScope(flow),
            ) ? (
              <TestRunsSidePane onRequestClose={toggleTestRunsSidePane} />
            ) : null,
            [LeftPaneOptions.Analytics]: (
              <AnalyticsSidePane onRequestClose={toggleAnalyticsSidePane} />
            ),
          }[openPane]
        }
      </div>
    );
  };

  const { flow } = useAuthoringContext();
  const flowTrafficPolicies = useTrafficPolicies(flow.id);
  const [showAssembleModal, setShowAssembleModal] = useState<
    false | DatasetPurpose
  >(false);

  return (
    <div className="relative flex h-full flex-row">
      {renderSideBarButtonsContainer()}
      {currentOpenPane !== null && renderSidePane(currentOpenPane)}
      {flowTrafficPolicies.data && (
        <AssembleDatasetModal
          flow={flow}
          open={!!showAssembleModal}
          purpose={showAssembleModal ? showAssembleModal : undefined}
          trafficPolicies={flowTrafficPolicies.data}
          onClose={() => setShowAssembleModal(false)}
        />
      )}
    </div>
  );
};
